import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@components/Common/Button';
import ButtonContainer from '@components/Common/ButtonContainer';
import Hint from '@components/Common/Hint';
import SkeepersIcon from '@components/Common/SkeepersIcon';
import ValuesPreview from '@components/Configuration/ValuesPreview';
import { getTypes } from '@components/Form/TemplateKeysForm';
import UploadInput from '@components/FormElement/UploadInput';

const getHint = (t) => (values, validValues) => {
  const notMatchedValues = values.filter(({ matched }) => !matched);
  const excludedValues = values.filter(({ matched, included }) => matched && !included);
  const emptyValues = values.filter(({ empty }) => empty);

  if (validValues.length === 0) {
    return {
      type: 'ERROR',
      message: t('No keys can be imported.'),
    };
  } else if (validValues.length < values.length) {
    return {
      type: 'WARNING',
      message: t('Only {{count}} out of {{total}} values will be imported ({{notMatched}} not matched, {{excluded}} excluded and {{empty}} empty).', {
        count: validValues.length,
        notMatched: notMatchedValues.length,
        empty: emptyValues.length,
        excluded: excludedValues.length,
        total: values.length,
      }),
    };
  }
};

const ImportPresetValuesForm = ({ keys = [], isUpdating = false, onSubmit = () => {} }) => {
  const { t } = useTranslation();
  const [fileLoading, setFileLoading] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [file, setFile] = useState(null);
  const [values, setValues] = useState(null);

  const onEdit = (file) => {
    setValues(null);
    setFileError(null);
    setFile(file);

    if (file) {
      setFileLoading(true);
      file
        .text()
        .then(JSON.parse)
        .then((json) =>
          setValues(
            Object.entries(json)
              .map(([originalKey, value]) => ({ originalKey, value, matchedKey: keys.find(({ name }) => name === originalKey) }))
              .map((value) => ({
                ...value,
                matched: !!value.matchedKey,
                included: !!value.matchedKey && value.matchedKey.isTranslatable,
                empty: value.value === null || value.value === '',
              }))
          )
        )
        .catch(setFileError)
        .finally(() => setFileLoading(false));
    }
  };

  const keyTypes = getTypes(t);
  // The final pivot values to export
  const validValues = values?.filter(({ included, empty }) => included && !empty);

  return (
    <>
      <UploadInput title={t('Source file')} value={file} onEdit={onEdit} local filenamesMode="HIDDEN" />
      {fileError && <Hint type="ERROR" message={t('Invalid file')} />}
      {fileLoading && (
        <div>
          <SkeepersIcon type="SPINNER" color="var(--layout-primary-color)" spin />
          {t('Reading file contents...')}
        </div>
      )}
      {values && (
        <>
          <h3>{t('Keys ({{count}})', { count: values.length })}</h3>
          {values.length > 0 ? (
            <>
              <ValuesPreview>
                {values.map(({ originalKey, matchedKey, included, matched, empty, value }) => (
                  <ValuesPreview.Item
                    key={originalKey}
                    icon={matchedKey ? keyTypes[matchedKey.type]?.icon || 'UNKNOWN' : 'CLOSE'}
                    name={originalKey}
                    value={value}
                    success={included}
                    warning={empty}
                    ignored={!matched || !included}
                  />
                ))}
              </ValuesPreview>
              <Hint {...getHint(t)(values, validValues)} />
            </>
          ) : (
            <p>{t('No keys found in file')}</p>
          )}
        </>
      )}
      <ButtonContainer position="CENTER">
        <Button
          label={t('Import')}
          disabled={!validValues || validValues.length === 0 || isUpdating}
          action={() => onSubmit(validValues)}
          loading={isUpdating}
        />
      </ButtonContainer>
    </>
  );
};

export default ImportPresetValuesForm;

ImportPresetValuesForm.propTypes = {
  isUpdating: PropTypes.bool,
  onSubmit: PropTypes.func,
};
